import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Paperclip, X } from 'lucide-react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
} from '@ai-tutor/ui';
import {
  bugReportSchema,
  BUG_REPORT_CATEGORIES,
  BUG_REPORT_CATEGORY_LABELS,
  MAX_SCREENSHOTS,
  MAX_SCREENSHOT_BYTES,
  type BugReportForm,
} from '@/lib/schemas/bug-report';
import { bugReportsApi } from '@/lib/api-client';
import { notify } from '@/lib/notify';

const DEFAULT_VALUES: BugReportForm = {
  category: 'bug',
  title: '',
  description: '',
  screenshots: [],
};

const formatBytes = (bytes: number) => `${(bytes / 1024).toFixed(0)} KB`;

export function ReportProblemSection() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<BugReportForm>({
    resolver: zodResolver(bugReportSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  // Watching directly (rather than wrapping the file input in FormField/
  // Controller) keeps the ref ours - Controller's spread otherwise overrides
  // ref={fileInputRef} and silently breaks the file picker.
  const screenshots = form.watch('screenshots');
  const screenshotsError = form.formState.errors.screenshots;
  const remaining = MAX_SCREENSHOTS - screenshots.length;

  const handleFilesPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (picked.length === 0) return;

    const next = [...screenshots];
    let skippedTooLarge = 0;
    let skippedOverLimit = 0;

    for (const file of picked) {
      if (next.length >= MAX_SCREENSHOTS) {
        skippedOverLimit += 1;
        continue;
      }
      if (file.size > MAX_SCREENSHOT_BYTES) {
        skippedTooLarge += 1;
        continue;
      }
      next.push(file);
    }

    form.setValue('screenshots', next, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (skippedTooLarge > 0) {
      notify.error(
        `${skippedTooLarge} file${skippedTooLarge > 1 ? 's' : ''} skipped (over 1 MB)`
      );
    }
    if (skippedOverLimit > 0) {
      notify.error(`Only ${MAX_SCREENSHOTS} screenshots allowed`);
    }
  };

  const removeScreenshot = (index: number) => {
    const next = screenshots.filter((_, i) => i !== index);
    form.setValue('screenshots', next, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const resetForm = () => {
    form.reset(DEFAULT_VALUES);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = async (values: BugReportForm) => {
    setIsSubmitting(true);
    try {
      await notify.promise(
        bugReportsApi.submit({
          category: values.category,
          title: values.title,
          description: values.description,
          screenshots: values.screenshots,
        }),
        {
          loading: 'Sending report…',
          success: 'Thanks - report received',
          error: "Couldn't send report",
        }
      );
      resetForm();
    } catch {
      // notify.promise already surfaces the error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='flex max-w-2xl flex-col gap-6'
      >
        <FormField
          control={form.control}
          name='category'
          render={({ field }) => (
            <FormItem>
              <FormLabel>What kind of issue is this?</FormLabel>
              <FormControl>
                <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
                  {BUG_REPORT_CATEGORIES.map(category => {
                    const selected = field.value === category;
                    return (
                      <button
                        type='button'
                        key={category}
                        // Colour alone said which category was chosen, so a
                        // screen reader announced four identical buttons.
                        aria-pressed={selected}
                        onClick={() => field.onChange(category)}
                        className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors max-sm:min-h-11 ${
                          selected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-card text-foreground hover:border-gray-300 hover:bg-accent dark:hover:border-gray-600'
                        }`}
                      >
                        {BUG_REPORT_CATEGORY_LABELS[category]}
                      </button>
                    );
                  })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='title'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input
                  placeholder='Short summary, e.g. "Quiz answer says 1990 but it was 1989"'
                  autoComplete='off'
                  maxLength={200}
                  className='bg-card'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='description'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <textarea
                  rows={6}
                  maxLength={5000}
                  placeholder="What happened? If it's incorrect content, paste the exact text or describe where it appeared."
                  className='flex w-full rounded-md border border-input bg-card px-3 py-2 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className='space-y-2'>
          <div className='flex items-end justify-between gap-2'>
            <Label htmlFor='bug-report-screenshots'>Screenshots</Label>
            <span className='text-xs text-muted-foreground'>
              {screenshots.length} / {MAX_SCREENSHOTS} · 1 MB each
            </span>
          </div>

          <input
            id='bug-report-screenshots'
            type='file'
            accept='image/png,image/jpeg,image/webp,image/gif'
            ref={fileInputRef}
            multiple
            className='sr-only'
            onChange={handleFilesPicked}
          />

          {screenshots.length > 0 && (
            <ul className='space-y-2'>
              {screenshots.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className='flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm'
                >
                  <span className='truncate pr-2 text-foreground'>
                    {file.name}{' '}
                    <span className='text-muted-foreground'>
                      ({formatBytes(file.size)})
                    </span>
                  </span>
                  <button
                    type='button'
                    onClick={() => removeScreenshot(index)}
                    className='tap-target rounded-sm p-1 text-muted-foreground hover:bg-accent'
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className='h-4 w-4' />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {remaining > 0 && (
            <button
              type='button'
              onClick={() => fileInputRef.current?.click()}
              className='inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 bg-card px-3 py-2 text-sm text-muted-foreground hover:border-gray-400 hover:bg-accent dark:border-gray-600 dark:hover:border-gray-500 max-sm:min-h-11'
            >
              <Paperclip className='h-4 w-4' />
              {screenshots.length === 0
                ? 'Attach screenshots'
                : `Add more (${remaining} left)`}
            </button>
          )}

          {screenshotsError?.message && (
            <p className='text-[0.8rem] font-medium text-destructive-emphasis'>
              {String(screenshotsError.message)}
            </p>
          )}
        </div>

        <div className='flex items-center justify-end gap-2 border-t border-border pt-4 max-sm:[&>button]:h-11 max-sm:[&>button]:flex-1'>
          <Button
            type='button'
            variant='outline'
            onClick={resetForm}
            disabled={isSubmitting || !form.formState.isDirty}
          >
            Reset
          </Button>
          <Button
            type='submit'
            disabled={isSubmitting || !form.formState.isValid}
          >
            {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Send report
          </Button>
        </div>
      </form>
    </Form>
  );
}
